import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { isLarge } from 'utils/breakpoints';
import FacilityInfoWindow from './FacilityInfoWindow';
import CustomOverlayComponent from 'components/core/library/map/customOverlayComponent';
import { Text } from 'components/core/Text';
import withViewportWidth from 'decorators/withViewportWidth';
import { MAP, GOOGLE } from 'components/core/library/map/constants';

export function enhance(WrappedComponent) {
	function WithFacilityMarkerLogic(props) {
		// withStateHandlers
		const [active, setActiveState] = React.useState(false);
		const [infoWindowOpen, setInfoWindowOpen] = React.useState(false);
		const [zIndex, setZIndex] = React.useState(props.zIndex);

		const setActive = React.useCallback(
			(isActive) => {
				setActiveState(isActive);
				setZIndex(isActive ? 2000 : props.zIndex);
			},
			[props.zIndex],
		);

		const handlePageClick = React.useCallback(() => {
			setInfoWindowOpen(false);
		}, []);

		// withHandlers - handleMarkerClick
		const handleMarkerClick = React.useCallback(() => {
			const {
				facility,
				onScrollToFacility,
				onMarkerHover,
				onShowMapFacilityCard,
				viewportWidth,
				position,
				isNewSearchPage,
			} = props;
			const map = props[MAP];
			const google = props[GOOGLE];

			if (onMarkerHover) {
				onMarkerHover(facility.id);
			}

			if (!isNewSearchPage) {
				// Old experience: show info window popup
				setInfoWindowOpen(true);
			} else {
				// New experience: scroll to facility or show facility card
				if (isLarge(viewportWidth)) {
					// On desktop: scroll to facility card in list
					if (onScrollToFacility) {
						onScrollToFacility(facility.id);
					}
				} else {
					// On mobile: show facility card at bottom of map and pan to marker
					if (onShowMapFacilityCard) {
						onShowMapFacilityCard(facility.id);
					}

					// Pan map on mobile to position marker above bottom card
					const hasMapObjects = google && map && position;
					if (hasMapObjects) {
						const markerLatLng = new google.maps.LatLng(
							position.lat,
							position.lng,
						);

						// First center on the marker
						map.panTo(markerLatLng);

						// Then offset to prevent facility card from covering marker
						const panOffset = 250;
						setTimeout(() => map.panBy(0, panOffset), 100);
					}
				}
			}
		}, [props]); // eslint-disable-line react-hooks/exhaustive-deps

		// withPropsOnChange(['highlighted'], ...) - sync active state with highlighted prop
		React.useEffect(() => {
			if (active !== !!props.highlighted) {
				setActive(!!props.highlighted);
			}
		}, [props.highlighted]); // eslint-disable-line react-hooks/exhaustive-deps

		// withPropsOnChange(['facility'], ...) - compute label
		const label = React.useMemo(() => {
			const { facility } = props;
			if (facility.cheapestPrice && facility.cheapestPrice.text) {
				return `$${facility.cheapestPrice.text.split('.')[0]}`;
			}

			const cheapestUnitPrice = facility.units.reduce(
				(min, unit) =>
					parseInt(unit.price?.text) < parseInt(min)
						? unit.price?.text
						: min,
				facility.units[0].price?.text,
			);

			return `$${cheapestUnitPrice?.split('.')[0]}`;
		}, [props.facility]);

		// lifecycle - componentDidMount
		const handleMarkerClickRef = React.useRef(handleMarkerClick);
		handleMarkerClickRef.current = handleMarkerClick;

		React.useEffect(() => {
			if (props.isPreselectedFacility) {
				handleMarkerClickRef.current();
			}
		}, []); // eslint-disable-line react-hooks/exhaustive-deps

		return (
			<WrappedComponent
				{...props}
				active={active}
				infoWindowOpen={infoWindowOpen}
				zIndex={zIndex}
				setActive={setActive}
				setInfoWindowOpen={setInfoWindowOpen}
				handlePageClick={handlePageClick}
				handleMarkerClick={handleMarkerClick}
				label={label}
			/>
		);
	}

	// getContext wrapper (class-based for legacy context API)
	class WithMapContext extends React.Component {
		render() {
			const contextProps = {
				[MAP]: this.context[MAP],
				[GOOGLE]: this.context[GOOGLE],
			};
			return (
				<WithFacilityMarkerLogic
					{...this.props}
					{...contextProps}
				/>
			);
		}
	}
	WithMapContext.contextTypes = {
		[MAP]: PropTypes.any,
		[GOOGLE]: PropTypes.any,
	};

	return withViewportWidth(WithMapContext);
}

export function FacilityMarker({
	position,
	label,
	setActive,
	active,
	zIndex,
	facility,
	handleMarkerClick,
	onMarkerHover,
	onMarkerLeave,
	isNewSearchPage,
	infoWindowOpen,
	viewportWidth,
	phone,
	getLocalPhoneNumberByZipCode,
	handlePageClick,
	ariaLabel,
}) {
	const handleMouseEnter = () => {
		if (onMarkerHover) {
			onMarkerHover(facility.id);
		} else {
			// Fallback for old behavior when not using global state
			setActive(true);
		}
	};

	const handleMouseLeave = () => {
		// Only clear highlight on old search page
		// New search page keeps marker active until another is hovered
		if (!isNewSearchPage && onMarkerLeave) {
			onMarkerLeave();
		} else {
			// Fallback for old behavior when not using global state
			setActive(false);
		}
	};

	return isNewSearchPage ? (
		<CustomOverlayComponent
			position={position}
			zIndex={zIndex}
		>
			<button
				className={classnames('facility-marker-icon', {
					active,
				})}
				onMouseEnter={handleMouseEnter}
				onClick={handleMarkerClick}
				type="button"
				aria-label={ariaLabel}
			>
				<Text className="marker-label">{label}</Text>
			</button>
		</CustomOverlayComponent>
	) : (
		<div>
			<CustomOverlayComponent
				position={position}
				zIndex={zIndex}
			>
				<button
					className={classnames('facility-marker-icon', {
						active,
						hidden: infoWindowOpen,
					})}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					onBlur={handleMouseLeave}
					onClick={handleMarkerClick}
					type="button"
					aria-label={ariaLabel}
				>
					<Text className="marker-label">{label}</Text>
				</button>
			</CustomOverlayComponent>
			{!!infoWindowOpen && (
				<FacilityInfoWindow
					idleDisabled
					viewportWidth={viewportWidth}
					position={position}
					facility={facility}
					onClickOutside={handlePageClick}
					phone={phone}
					getLocalPhoneNumberByZipCode={getLocalPhoneNumberByZipCode}
				/>
			)}
		</div>
	);
}

FacilityMarker.propTypes = {
	position: PropTypes.shape({ lat: PropTypes.number, lng: PropTypes.number }),
	label: PropTypes.string,
	setActive: PropTypes.func,
	active: PropTypes.bool,
	zIndex: PropTypes.number,
	facility: PropTypes.object,
	handleMarkerClick: PropTypes.func,
	onMarkerHover: PropTypes.func,
	onMarkerLeave: PropTypes.func,
	onScrollToFacility: PropTypes.func,
	onShowMapFacilityCard: PropTypes.func,
	viewportWidth: PropTypes.number,
	isNewSearchPage: PropTypes.bool,
	infoWindowOpen: PropTypes.bool,
	phone: PropTypes.object,
	getLocalPhoneNumberByZipCode: PropTypes.func,
	handlePageClick: PropTypes.func,
	ariaLabel: PropTypes.string,
};

export default enhance(FacilityMarker);
